import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntityList } from '@backstage/plugin-catalog-react';
import React from 'react';
import { convertListToCsv } from '@acuity-brands/mojito.cantina.csv-generator';
import { Button } from '@material-ui/core';

const transformFiltersForGetEntities = (
  filters: Record<string, any>,
): Record<string, any> => {
  const transformedFilters: Record<string, any> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      // Handle fields with a single value
      if (key === 'kind' && value.value !== undefined) {
        transformedFilters.kind = value.value;
      }

      // Handle fields with multiple values (arrays)
      if (key === 'owners' && Array.isArray(value.values)) {
        transformedFilters['relations.ownedBy'] = value.values;
      }

      if (key === 'lifecycles' && Array.isArray(value.values)) {
        transformedFilters['spec.lifecycle'] = value.values;
      }

      if (key === 'namespace' && Array.isArray(value.values)) {
        transformedFilters['metadata.namespace'] = value.values[0];
      }
    }
  });

  return transformedFilters;
};

const CsvExportButton: React.FC = () => {
  const entityListContext = useEntityList();
  const catalogApi = useApi(catalogApiRef);

  const { filters } = entityListContext;

  const handleClick = async () => {
    const transformedFilters = transformFiltersForGetEntities(filters);
    const response = await catalogApi.getEntities({
      filter: transformedFilters,

      fields: [
        'metadata.name',
        'metadata.namespace',
        'metadata.description',
        'metadata.annotations',
        'kind',
        'spec',
      ],
    });

    const allEntities = response.items;

    // filter out type values since we don't get those as part of the filters values
    const filteredEntities = allEntities.filter(entity => {
      const typeValue = entityListContext.filters.type?.value;

      if (Array.isArray(typeValue) && typeValue.length > 0) {
        return typeValue.includes(entity.spec?.type?.toString() ?? '');
      }

      return true;
    });

    // Transform entities into a flat structure
    const flattenedEntities = filteredEntities.map(entity => ({
      name: entity.metadata.name,
      owner: entity.spec?.owner?.toString() || 'Unknown',
      description: entity.metadata.description?.replace('\n', ' '),
      lifecycle: entity.spec?.lifecycle?.toString() || 'Unknown',
      namespace: entity.metadata.namespace,
      kind: entity.kind,
      type: entity.spec?.type?.toString() || 'Unknown',
      tags: entity.metadata.tags?.join(', ') || 'None',
    }));

    const csvData = convertListToCsv(flattenedEntities);
    const blob = new Blob([csvData], { type: 'text/csv' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `entities-${new Date().toISOString()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Button
        style={{ width: '50%' }}
        variant="contained"
        color="primary"
        onClick={handleClick}
      >
        Export
      </Button>
    </div>
  );
};

export default CsvExportButton;
